import { useMemo, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { CalendarListEntry } from '@/types/calendar';
import { COLOR_CHOICES } from './calendarColorUtils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

function groupCalendars(calendars: Record<string, CalendarListEntry>, order: string[]) {
  const groups = new Map<string, { label: string; entries: CalendarListEntry[] }>();
  order.forEach((uid) => {
    const calendar = calendars[uid];
    if (!calendar) {
      return;
    }
    const accountId = calendar.accountId ?? 'unknown';
    if (!groups.has(accountId)) {
      groups.set(accountId, { label: calendar.accountEmail ?? accountId, entries: [] });
    }
    groups.get(accountId)!.entries.push(calendar);
  });
  return Array.from(groups.values());
}

interface CalendarVisibilitySelectorProps {
  title?: string;
  description?: string;
  calendars: Record<string, CalendarListEntry>;
  calendarOrder: string[];
  visibleCalendarUids: string[];
  onToggleCalendar: (calendarUid: string, visible?: boolean) => void;
  onSetVisibleCalendars: (calendarUids: string[]) => void;
  onSelectCalendarColor: (calendarUid: string, color: string) => Promise<void> | void;
  onRefreshCalendars?: () => void;
  refreshingCalendars?: boolean;
  className?: string;
  emptyDescription?: string;
}

export function CalendarVisibilitySelector({
  title = 'Calendars',
  description,
  calendars,
  calendarOrder,
  visibleCalendarUids,
  onToggleCalendar,
  onSetVisibleCalendars,
  onSelectCalendarColor,
  className,
  emptyDescription = 'Connect a Google account to load calendars.',
}: CalendarVisibilitySelectorProps) {
  const groupedCalendars = useMemo(() => groupCalendars(calendars, calendarOrder), [calendars, calendarOrder]);
  const activeSet = useMemo(() => new Set(visibleCalendarUids), [visibleCalendarUids]);
  const [colorSavingUid, setColorSavingUid] = useState<string | null>(null);

  const handleShowAllCalendars = () => {
    if (!calendarOrder.length) {
      return;
    }
    onSetVisibleCalendars(calendarOrder);
  };

  const handleHideAllCalendars = () => {
    onSetVisibleCalendars([]);
  };

  const handleColorSelect = async (uid: string, color: string) => {
    setColorSavingUid(uid);
    try {
      await onSelectCalendarColor(uid, color);
    } finally {
      setColorSavingUid(null);
    }
  };

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center justify-between gap-1">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
          
        </div>
        <div className="flex items-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShowAllCalendars}
                disabled={!calendarOrder.length}
                aria-label="Show all calendars"
                className="text-xs gap-1"
              >
                <Eye className="h-4 w-4" /> All
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Show all calendars
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleHideAllCalendars}
                disabled={activeSet.size === 0}
                aria-label="Hide all calendars"
                className="text-xs gap-1 -ml-1"
              >
                <EyeOff className="h-4 w-4" /> None
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Hide all calendars
            </TooltipContent>
          </Tooltip>
          
        </div>
        
      </div>
      {description ? <p className="text-xs text-muted-foreground/80">{description}</p> : null}
      {groupedCalendars.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyDescription}</p>
      ) : (
        <div className="space-y-4 w-full max-w-[300px]">
          {groupedCalendars.map((group) => (
            <div key={group.label} className="space-y-2">
              <div className="space-y-1">
                {group.entries.map((calendar) => {
                  const isVisible = activeSet.has(calendar.uid);
                  const color = calendar.backgroundColor ?? '#94a3b8';
                  const handleToggle = () => onToggleCalendar(calendar.uid, !isVisible);
                  const handleKeyToggle = (event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleToggle();
                    }
                  };
                  const stopPropagation = (event: MouseEvent | KeyboardEvent) => {
                    event.stopPropagation();
                  };
                  return (
                    <div
                      key={calendar.uid}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isVisible}
                      onClick={handleToggle}
                      onKeyDown={handleKeyToggle}
                      className={cn(
                        'cursor-pointer flex items-center justify-between rounded-2xl border px-3 py-1 text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        isVisible
                          ? 'shadow-sm'
                          : 'border-border/60 text-muted-foreground/80 hover:text-foreground hover:border-primary/30 hover:bg-muted/40',
                      )}
                      style={
                        isVisible
                          ? {
                              borderColor: color,
                              backgroundColor: `${color}1a`,
                            }
                          : undefined
                      }
                    >
                      <div className="flex items-center gap-3">
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="h-3.5 w-3.5 rounded-full border border-border/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              style={{ backgroundColor: color }}
                              onClick={stopPropagation}
                              onKeyDown={stopPropagation}
                            >
                              {colorSavingUid === calendar.uid && (
                                <Loader2 className="h-3 w-3 animate-spin text-white" />
                              )}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-46" onOpenAutoFocus={(event) => event.preventDefault()}>
                            <p className="mb-2 text-xs font-semibold text-muted-foreground">Calendar color</p>
                            <div className="flex flex-wrap gap-1.5">
                              {COLOR_CHOICES.map((candidate) => (
                                <button
                                  key={candidate}
                                  type="button"
                                  className="h-5 w-5 rounded-full border border-border/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                  style={{ backgroundColor: candidate }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void handleColorSelect(calendar.uid, candidate);
                                  }}
                                  aria-label={`Set color ${candidate}`}
                                />
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        <div>
                          <p className="font-medium text-foreground line-clamp-1">{calendar.summary}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
